// @ts-ignore
import { ValidationErrors } from "final-form";
import { _, _any } from "i18n";
import { Field, Form } from "react-final-form";
import { Badge } from "ui-kit";
import { GamePageResponse } from "../../types";
import { getTaggedTeamSide } from "../../utils/teamTagged";
import "./PlayerStatsExport.css";
import { PlayerStatCategory } from "./definition";

interface Option {
	key: string;
	value: string;
	label: string;
}

interface PlayerStatsExportProps {
	gameData: GamePageResponse;
	category: PlayerStatCategory;
	gamePeriods: { key: string; label: string }[];
	selectedPeriods: string[];
	team: number;
	onExportPlayerStats: (format: "csv" | "xlsx", home: boolean, away: boolean, categories: PlayerStatCategory[], periods: string[]) => void;
}

interface FormValues {
	format: "csv" | "xlsx";
	home: boolean;
	away: boolean;
	periods: boolean[];
	summary: boolean;
	offensive: boolean;
	defensive: boolean;
	distribution: boolean;
	goalkeeper: boolean;
}

export function PlayerStatsExport({ gameData, category, gamePeriods, selectedPeriods, team, onExportPlayerStats }: PlayerStatsExportProps) {
	const taggedSide = getTaggedTeamSide(gameData.game?.teamTagged);

	const onSubmit = (values: FormValues) => {
		const categories: PlayerStatCategory[] = [];

		if (values.summary) {
			categories.push(PlayerStatCategory.Summary);
		}

		if (values.offensive) {
			categories.push(PlayerStatCategory.Offensive);
		}

		if (values.defensive) {
			categories.push(PlayerStatCategory.Defensive);
		}

		if (values.distribution) {
			categories.push(PlayerStatCategory.Distribution);
		}

		if (values.goalkeeper) {
			categories.push(PlayerStatCategory.Goalkeeper);
		}

		const exportHome = taggedSide !== null ? taggedSide === "home" : values.home;
		const exportAway = taggedSide !== null ? taggedSide === "away" : values.away;

		onExportPlayerStats(
			values.format,
			exportHome,
			exportAway,
			categories,
			gamePeriods.filter((gp, index) => values.periods[index]).map((gp) => gp.key)
		);
	};

	const onValidate = (values: FormValues): ValidationErrors | Promise<ValidationErrors> => {
		let categoriesCount = 0;
		const errors: any = {};

		Object.values(PlayerStatCategory).forEach((cat) => {
			if (values[cat] === true) {
				categoriesCount++;
			}
		});

		const hasTeam = taggedSide !== null || values.home === true || values.away === true;

		if (categoriesCount === 0 || hasTeam === false || values.periods.filter((p) => p).length === 0) {
			errors.format = "Invalid form";
		}

		return errors;
	};

	const exportFormValues = {
		format: "csv",
		home: taggedSide !== null ? taggedSide === "home" : team === gameData.homeGameTeam.id,
		away: taggedSide !== null ? taggedSide === "away" : team === gameData.awayGameTeam.id,
		summary: category === PlayerStatCategory.Summary,
		offensive: category === PlayerStatCategory.Offensive,
		defensive: category === PlayerStatCategory.Defensive,
		distribution: category === PlayerStatCategory.Distribution,
		goalkeeper: category === PlayerStatCategory.Goalkeeper,
		periods: gamePeriods.map((gp) => selectedPeriods.includes(gp.key)),
	} as FormValues;

	const periodOptions = gamePeriods.map((gp) => ({ key: gp.key, value: gp.key, label: gp.label }) as Option);

	return (
		<Form
			initialValues={exportFormValues}
			onSubmit={onSubmit}
			validate={onValidate}
			render={({ handleSubmit, valid }) => (
				<form onSubmit={handleSubmit} className="player-stats-export">
					<div className="player-stats-export__title">{_("stats.export.title")}</div>
					<Field name="format">
						{({ input, meta }) => (
							<div className="player-stats-export__formats">
								<span key="csv">
									<input id={`export-format-csv`} type="radio" {...input} value="csv" />
									<label htmlFor={`export-format-csv`}>
										<Badge
											label=".csv"
											variant={input.value === "csv" ? "secondary" : "default"}
											outline={input.value !== "csv"}
											className="player-stats-export__format"
											onClick={() => {}}
											block={true}
										/>
									</label>
								</span>
								<span key="xlsx">
									<input id={`export-format-xlsx`} type="radio" {...input} value="xlsx" />
									<label htmlFor={`export-format-xlsx`}>
										<Badge
											label=".xlsx"
											variant={input.value === "xlsx" ? "secondary" : "default"}
											outline={input.value !== "xlsx"}
											onClick={() => {}}
											className="player-stats-export__format"
											block={true}
										/>
									</label>
								</span>
							</div>
						)}
					</Field>
					<div className="event-export__title">{_("stats.export.periods")}</div>
					<div className="event-export__blocks">
						{periodOptions.map((period: Option, index) => (
							<Field name={`periods[${index}]`} key={`period-${period.key}`}>
								{({ input, meta }) => (
									<div key={period.key}>
										<input id={`export-period-${period.key}`} type="checkbox" {...input} checked={!!input.value} />
										<label htmlFor={`export-period-${period.key}`}>
											<Badge
												label={period.label}
												variant={input.value === true ? "secondary" : "default"}
												outline={!input.value}
												block={true}
												icon={!!input.value ? "check" : undefined}
												onClick={() => {}}
											/>
										</label>
									</div>
								)}
							</Field>
						))}
					</div>
					{taggedSide === null && (
						<>
							<div className="player-stats-export__title">{_("stats.export.team")}</div>
							<div className="player-stats-export__blocks">
								<Field name="home">
									{({ input, meta }) => (
										<div>
											<input type="checkbox" id="export-team-home" {...input} />
											<label htmlFor="export-team-home">
												<Badge
													label={gameData.homeGameTeam.clubName || "Home team"}
													variant={input.value === true ? "secondary" : "default"}
													outline={!input.value}
													block={true}
													icon={!!input.value ? "check" : undefined}
													onClick={() => {}}
												/>
											</label>
										</div>
									)}
								</Field>
								<Field name="away">
									{({ input, meta }) => (
										<div>
											<input type="checkbox" id="export-team-away" {...input} />
											<label htmlFor="export-team-away">
												<Badge
													label={gameData.awayGameTeam.clubName || "Away team"}
													variant={input.value === true ? "secondary" : "default"}
													outline={!input.value}
													block={true}
													icon={!!input.value ? "check" : undefined}
													onClick={() => {}}
												/>
											</label>
										</div>
									)}
								</Field>
							</div>
						</>
					)}
					<div className="player-stats-export__title">{_("stats.export.statsCategories")}</div>
					<div className="player-stats-export__blocks">
						{Object.values(PlayerStatCategory).map((category: PlayerStatCategory) => (
							<Field name={category.toString()} key={`category-${category}`}>
								{({ input, meta }) => (
									<div key={category}>
										<input type="checkbox" id={`export-category-${category}`} {...input} />
										<label htmlFor={`export-category-${category}`}>
											<Badge
												label={_any(`gamePage.stats.category.${category}`)}
												variant={input.value === true ? "secondary" : "default"}
												outline={!input.value}
												block={true}
												icon={!!input.value ? "check" : undefined}
												onClick={() => {}}
											/>
										</label>
									</div>
								)}
							</Field>
						))}
					</div>
					<button type="submit" disabled={!valid}>
						<Badge label="stats.export.submit" block={true} variant="primary" onClick={() => {}} disabled={!valid} />
					</button>
				</form>
			)}
		/>
	);
}
